import React, { useState } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { mockData } from '../../data/mockData';
import { 
  BarChart3, TrendingUp, TrendingDown, ArrowUpRight, ArrowDownRight, 
  Calendar, ChevronRight, Check, Minus
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
  LineChart, Line, LabelList
} from 'recharts';

/**
 * FinanceMultiPeriodComparative - Multi-period comparison page for Finance
 * Allows board members to compare financial performance across multiple periods
 */

const colors = {
  primary: '#0A192F',
  gold: '#C5A059',
  positive: '#059669',
  negative: '#DC2626',
  neutral: '#64748B',
  chart: ['#0A192F', '#C5A059', '#3B82F6', '#10B981']
};

const FinanceMultiPeriodComparative = ({ language }) => {
  const [selectedPeriods, setSelectedPeriods] = useState(['2026-Q1', '2026-Q1-Budget', '2025-Q1']);
  const [selectedIndicator, setSelectedIndicator] = useState('revenue');
  
  // Period structure for table display
  // Annual periods: rows by year
  const annualPeriods = [
    { year: 2026, real: null, budget: '2026-Budget' },
    { year: 2025, real: '2025-FY', budget: '2025-Budget' },
    { year: 2024, real: '2024-FY', budget: null },
  ];

  // Quarterly periods: rows by quarter (most recent first)
  const quarterlyPeriods = [
    { quarter: '4T26', real: null, budget: '2026-Q4-Budget' },
    { quarter: '3T26', real: null, budget: '2026-Q3-Budget' },
    { quarter: '2T26', real: null, budget: '2026-Q2-Budget' },
    { quarter: '1T26', real: '2026-Q1', budget: '2026-Q1-Budget' },
    { quarter: '4T25', real: '2025-Q4', budget: '2025-Q4-Budget' },
    { quarter: '3T25', real: '2025-Q3', budget: '2025-Q3-Budget' },
    { quarter: '2T25', real: '2025-Q2', budget: '2025-Q2-Budget' },
    { quarter: '1T25', real: '2025-Q1', budget: '2025-Q1-Budget' },
  ];

  // All available periods with metadata
  const availablePeriods = [
    // Annual
    { id: '2024-FY', label: { pt: '2024', en: '2024' }, year: 2024, category: 'annual', isReal: true },
    { id: '2025-FY', label: { pt: '2025', en: '2025' }, year: 2025, category: 'annual', isReal: true },
    { id: '2025-Budget', label: { pt: 'Orç. 2025', en: 'Budget 2025' }, year: 2025, category: 'annual', isReal: false },
    { id: '2026-Budget', label: { pt: 'Orç. 2026', en: 'Budget 2026' }, year: 2026, category: 'annual', isReal: false },
    // Quarterly 2025
    { id: '2025-Q1', label: { pt: '1T25', en: 'Q1 25' }, year: 2025, quarter: 1, category: 'quarter', isReal: true },
    { id: '2025-Q2', label: { pt: '2T25', en: 'Q2 25' }, year: 2025, quarter: 2, category: 'quarter', isReal: true },
    { id: '2025-Q3', label: { pt: '3T25', en: 'Q3 25' }, year: 2025, quarter: 3, category: 'quarter', isReal: true },
    { id: '2025-Q4', label: { pt: '4T25', en: 'Q4 25' }, year: 2025, quarter: 4, category: 'quarter', isReal: true },
    { id: '2025-Q1-Budget', label: { pt: 'Orç. 1T25', en: 'Budget Q1 25' }, year: 2025, quarter: 1, category: 'quarter', isReal: false },
    { id: '2025-Q2-Budget', label: { pt: 'Orç. 2T25', en: 'Budget Q2 25' }, year: 2025, quarter: 2, category: 'quarter', isReal: false },
    { id: '2025-Q3-Budget', label: { pt: 'Orç. 3T25', en: 'Budget Q3 25' }, year: 2025, quarter: 3, category: 'quarter', isReal: false },
    { id: '2025-Q4-Budget', label: { pt: 'Orç. 4T25', en: 'Budget Q4 25' }, year: 2025, quarter: 4, category: 'quarter', isReal: false },
    // Quarterly 2026
    { id: '2026-Q1', label: { pt: '1T26', en: 'Q1 26' }, year: 2026, quarter: 1, category: 'quarter', isReal: true },
    { id: '2026-Q1-Budget', label: { pt: 'Orç. 1T26', en: 'Budget Q1 26' }, year: 2026, quarter: 1, category: 'quarter', isReal: false },
    { id: '2026-Q2-Budget', label: { pt: 'Orç. 2T26', en: 'Budget Q2 26' }, year: 2026, quarter: 2, category: 'quarter', isReal: false },
    { id: '2026-Q3-Budget', label: { pt: 'Orç. 3T26', en: 'Budget Q3 26' }, year: 2026, quarter: 3, category: 'quarter', isReal: false },
    { id: '2026-Q4-Budget', label: { pt: 'Orç. 4T26', en: 'Budget Q4 26' }, year: 2026, quarter: 4, category: 'quarter', isReal: false },
  ];

  // Get the category of currently selected periods
  const getSelectedCategory = () => {
    if (selectedPeriods.length === 0) return null;
    const firstPeriod = availablePeriods.find(p => p.id === selectedPeriods[0]);
    return firstPeriod?.category || null;
  };

  // Check if a period can be selected
  const canSelectPeriod = (periodId) => {
    if (!periodId) return false;
    const period = availablePeriods.find(p => p.id === periodId);
    if (!period) return false;
    const selectedCategory = getSelectedCategory();
    if (!selectedCategory || selectedPeriods.length === 0) return true;
    return period.category === selectedCategory;
  };

  // Toggle period selection - allows deselection by clicking again
  const togglePeriod = (periodId) => {
    if (!periodId) return;
    
    if (selectedPeriods.includes(periodId)) {
      // Deselect - always allowed
      setSelectedPeriods(selectedPeriods.filter(p => p !== periodId));
    } else {
      // Select - check constraints
      if (selectedPeriods.length < 4 && canSelectPeriod(periodId)) {
        setSelectedPeriods([...selectedPeriods, periodId]);
      }
    }
  };

  // Sort selected periods: most recent first, Real before Budget
  const getSortedSelectedPeriods = () => {
    return [...selectedPeriods].sort((a, b) => {
      const periodA = availablePeriods.find(p => p.id === a);
      const periodB = availablePeriods.find(p => p.id === b);
      if (!periodA || !periodB) return 0;
      
      // Sort by year descending
      if (periodB.year !== periodA.year) return periodB.year - periodA.year;
      // Then by quarter descending (if applicable)
      if (periodA.quarter && periodB.quarter && periodB.quarter !== periodA.quarter) {
        return periodB.quarter - periodA.quarter;
      }
      // Real before Budget
      if (periodA.isReal && !periodB.isReal) return -1;
      if (!periodA.isReal && periodB.isReal) return 1;
      return 0;
    });
  };

  const sortedSelectedPeriods = getSortedSelectedPeriods();
  const selectedCategory = getSelectedCategory();

  // Available indicators for selection
  const availableIndicators = [
    { key: 'revenue', label: { pt: 'Receita', en: 'Revenue' }, unit: 'M USD', color: '#0A192F' },
    { key: 'ebitda', label: { pt: 'EBITDA', en: 'EBITDA' }, unit: 'M USD', color: '#C5A059' },
    { key: 'ebitdaMargin', label: { pt: 'Margem EBITDA', en: 'EBITDA Margin' }, unit: '%', color: '#3B82F6' },
    { key: 'netIncome', label: { pt: 'Resultado Líquido', en: 'Net Income' }, unit: 'M USD', color: '#10B981' },
    { key: 'opex', label: { pt: 'Custos Operacionais', en: 'Operating Costs' }, unit: 'M USD', color: '#EF4444', inverse: true },
    { key: 'carats', label: { pt: 'Quilates Vendidos', en: 'Carats Sold' }, unit: 'cts', color: '#8B5CF6' },
    { key: 'costPerCarat', label: { pt: 'Custo/Quilate', en: 'Cost/Carat' }, unit: 'USD', color: '#F59E0B', inverse: true },
    { key: 'cashGeneration', label: { pt: 'Geração de Caixa', en: 'Cash Generation' }, unit: 'M USD', color: '#06B6D4' },
  ];

  // Mock data for comparison (in a real scenario, this would come from API)
  const periodData = {
    // Annual
    '2024-FY': { revenue: 312.8, ebitda: 125.1, ebitdaMargin: 40.0, netIncome: 68.4, opex: 187.7, carats: 168200, costPerCarat: 1116, cashGeneration: 102.3 },
    '2025-FY': { revenue: 347.4, ebitda: 144.3, ebitdaMargin: 41.5, netIncome: 79.7, opex: 203.1, carats: 182500, costPerCarat: 1113, cashGeneration: 119.0 },
    '2025-Budget': { revenue: 360.0, ebitda: 151.2, ebitdaMargin: 42.0, netIncome: 84.0, opex: 208.8, carats: 190000, costPerCarat: 1099, cashGeneration: 125.0 },
    '2026-Budget': { revenue: 380.0, ebitda: 159.6, ebitdaMargin: 42.0, netIncome: 88.5, opex: 220.4, carats: 195000, costPerCarat: 1130, cashGeneration: 132.0 },
    // Quarterly 2025 Real
    '2025-Q1': { revenue: 82.5, ebitda: 33.8, ebitdaMargin: 41.0, netIncome: 18.2, opex: 48.7, carats: 43500, costPerCarat: 1120, cashGeneration: 27.8 },
    '2025-Q2': { revenue: 85.3, ebitda: 35.2, ebitdaMargin: 41.3, netIncome: 19.1, opex: 50.1, carats: 44800, costPerCarat: 1118, cashGeneration: 29.2 },
    '2025-Q3': { revenue: 88.7, ebitda: 37.1, ebitdaMargin: 41.8, netIncome: 20.5, opex: 51.6, carats: 46200, costPerCarat: 1117, cashGeneration: 30.5 },
    '2025-Q4': { revenue: 90.9, ebitda: 38.2, ebitdaMargin: 42.0, netIncome: 21.9, opex: 52.7, carats: 48000, costPerCarat: 1098, cashGeneration: 31.5 },
    // Quarterly 2025 Budget
    '2025-Q1-Budget': { revenue: 85.0, ebitda: 35.7, ebitdaMargin: 42.0, netIncome: 19.8, opex: 49.3, carats: 45000, costPerCarat: 1095, cashGeneration: 29.5 },
    '2025-Q2-Budget': { revenue: 88.0, ebitda: 37.0, ebitdaMargin: 42.0, netIncome: 20.5, opex: 51.0, carats: 46500, costPerCarat: 1097, cashGeneration: 30.5 },
    '2025-Q3-Budget': { revenue: 91.0, ebitda: 38.2, ebitdaMargin: 42.0, netIncome: 21.2, opex: 52.8, carats: 48000, costPerCarat: 1100, cashGeneration: 31.5 },
    '2025-Q4-Budget': { revenue: 96.0, ebitda: 40.3, ebitdaMargin: 42.0, netIncome: 22.5, opex: 55.7, carats: 50500, costPerCarat: 1103, cashGeneration: 33.5 },
    // Quarterly 2026 Real
    '2026-Q1': { revenue: 85.2, ebitda: 34.1, ebitdaMargin: 40.0, netIncome: 18.5, opex: 51.1, carats: 42500, costPerCarat: 1202, cashGeneration: 28.3 },
    // Quarterly 2026 Budget
    '2026-Q1-Budget': { revenue: 90.0, ebitda: 37.8, ebitdaMargin: 42.0, netIncome: 21.0, opex: 52.2, carats: 45000, costPerCarat: 1160, cashGeneration: 31.0 },
    '2026-Q2-Budget': { revenue: 93.0, ebitda: 39.1, ebitdaMargin: 42.0, netIncome: 21.7, opex: 53.9, carats: 46500, costPerCarat: 1158, cashGeneration: 32.2 },
    '2026-Q3-Budget': { revenue: 96.0, ebitda: 40.3, ebitdaMargin: 42.0, netIncome: 22.4, opex: 55.7, carats: 48000, costPerCarat: 1160, cashGeneration: 33.3 },
    '2026-Q4-Budget': { revenue: 101.0, ebitda: 42.4, ebitdaMargin: 42.0, netIncome: 23.4, opex: 58.6, carats: 50500, costPerCarat: 1162, cashGeneration: 35.5 },
  };

  // KPI definitions
  const kpiDefinitions = [
    { key: 'revenue', label: { pt: 'Receita', en: 'Revenue' }, unit: 'M USD', format: (v) => v.toFixed(1) },
    { key: 'ebitda', label: { pt: 'EBITDA', en: 'EBITDA' }, unit: 'M USD', format: (v) => v.toFixed(1) },
    { key: 'ebitdaMargin', label: { pt: 'Margem EBITDA', en: 'EBITDA Margin' }, unit: '%', format: (v) => v.toFixed(1) },
    { key: 'netIncome', label: { pt: 'Resultado Líquido', en: 'Net Income' }, unit: 'M USD', format: (v) => v.toFixed(1) },
    { key: 'opex', label: { pt: 'Custos Operacionais', en: 'Operating Costs' }, unit: 'M USD', format: (v) => v.toFixed(1), inverse: true },
    { key: 'carats', label: { pt: 'Quilates Vendidos', en: 'Carats Sold' }, unit: 'cts', format: (v) => v.toLocaleString() },
    { key: 'costPerCarat', label: { pt: 'Custo/Quilate', en: 'Cost/Carat' }, unit: 'USD', format: (v) => v.toLocaleString(), inverse: true },
    { key: 'cashGeneration', label: { pt: 'Geração de Caixa', en: 'Cash Generation' }, unit: 'M USD', format: (v) => v.toFixed(1) },
  ];

  // Get selected indicator details
  const currentIndicator = availableIndicators.find(i => i.key === selectedIndicator);

  // Calculate variance between first sorted period (most recent) and others
  const getVariance = (kpi, periodId) => {
    if (periodId === sortedSelectedPeriods[0]) return null;
    const base = periodData[sortedSelectedPeriods[0]][kpi.key];
    const current = periodData[periodId][kpi.key];
    const variance = ((current - base) / base * 100);
    return variance;
  };

  // Prepare chart data for selected indicator (use sorted periods)
  const chartData = sortedSelectedPeriods.map(periodId => {
    const period = availablePeriods.find(p => p.id === periodId);
    const data = periodData[periodId];
    return {
      period: period.label[language],
      value: data[selectedIndicator],
      periodId: periodId
    };
  }).reverse();

  return (
    <div className="space-y-8" data-testid="finance-multi-period-comparative">
      {/* Page Header */}
      <div className="mb-6">
        <h1 
          className="text-[32px] leading-[40px] font-semibold tracking-[-0.3px] mb-2 text-primary"
          style={{ fontFamily: '"Source Serif 4", Georgia, serif' }}
        >
          {language === 'pt' ? 'Comparativo Multi-Período' : 'Multi-Period Comparison'}
        </h1>
        <p className="text-[14px] leading-[20px] text-slate-500 mb-4">
          {language === 'pt' 
            ? 'Análise comparativa de performance financeira entre períodos selecionados'
            : 'Comparative analysis of financial performance across selected periods'}
        </p>
      </div>

      {/* Combined Selector - Periods (Left) and Indicator (Right) */}
      <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
        <div className="grid grid-cols-2 gap-6">
          {/* Left: Period Selector */}
          <div className="border-r border-slate-200 pr-6">
            <h2 className="font-serif text-sm font-semibold text-primary mb-3 flex items-center gap-2">
              <Calendar className="w-4 h-4 text-gold" />
              {language === 'pt' ? 'Selecionar Períodos (máx. 4)' : 'Select Periods (max. 4)'}
            </h2>
            
            {/* Note about comparable periods */}
            <p className="text-[10px] text-slate-500 mb-2 bg-slate-50 p-2 rounded border border-slate-200">
              {language === 'pt' 
                ? '⚠️ Só é possível comparar períodos do mesmo tipo: trimestre com trimestre ou ano com ano.'
                : '⚠️ Only comparable periods can be selected: quarter with quarter or year with year.'}
            </p>
            
            {/* Selection mode indicator */}
            {selectedPeriods.length > 0 && (
              <p className="text-[10px] text-slate-500 mb-2 italic">
                {language === 'pt' 
                  ? `Modo: ${selectedCategory === 'quarter' ? 'Trimestral' : 'Anual'} — clique para desselecionar`
                  : `Mode: ${selectedCategory === 'quarter' ? 'Quarterly' : 'Annual'} — click to deselect`}
              </p>
            )}
            
            {/* Period Selection Table */}
            <div className="grid grid-cols-2 gap-4">
              {/* Annual Column */}
              <div className={`border rounded-lg overflow-hidden ${selectedCategory === 'quarter' ? 'opacity-40' : ''}`}>
                <div className="bg-emerald-600 text-white text-[10px] font-bold text-center py-1.5">
                  {language === 'pt' ? 'ANO' : 'YEAR'}
                </div>
                <div className="grid grid-cols-2 text-[9px] font-semibold text-center border-b bg-slate-50">
                  <div className="py-1 border-r text-emerald-700">{language === 'pt' ? 'Real' : 'Actual'}</div>
                  <div className="py-1 text-amber-700">{language === 'pt' ? 'Orçamento' : 'Budget'}</div>
                </div>
                {annualPeriods.map((row, idx) => (
                  <div key={row.year} className={`grid grid-cols-2 text-[10px] ${idx % 2 === 0 ? 'bg-white' : 'bg-slate-50'}`}>
                    <div className="py-1.5 px-1 border-r flex justify-center">
                      {row.real ? (
                        <button
                          onClick={() => togglePeriod(row.real)}
                          disabled={selectedCategory === 'quarter'}
                          className={`px-2 py-0.5 rounded transition-all ${
                            selectedPeriods.includes(row.real)
                              ? 'bg-emerald-500 text-white font-semibold'
                              : 'hover:bg-emerald-100 text-slate-700'
                          } ${selectedCategory === 'quarter' ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                        >
                          {row.year}
                        </button>
                      ) : (
                        <span className="text-slate-300">—</span>
                      )}
                    </div>
                    <div className="py-1.5 px-1 flex justify-center">
                      {row.budget ? (
                        <button
                          onClick={() => togglePeriod(row.budget)}
                          disabled={selectedCategory === 'quarter'}
                          className={`px-2 py-0.5 rounded transition-all ${
                            selectedPeriods.includes(row.budget)
                              ? 'bg-amber-500 text-white font-semibold'
                              : 'hover:bg-amber-100 text-slate-700'
                          } ${selectedCategory === 'quarter' ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                        >
                          {row.year}
                        </button>
                      ) : (
                        <span className="text-slate-300">—</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Quarterly Column */}
              <div className={`border rounded-lg overflow-hidden ${selectedCategory === 'annual' ? 'opacity-40' : ''}`}>
                <div className="bg-blue-600 text-white text-[10px] font-bold text-center py-1.5">
                  {language === 'pt' ? 'TRIMESTRE' : 'QUARTER'}
                </div>
                <div className="grid grid-cols-2 text-[9px] font-semibold text-center border-b bg-slate-50">
                  <div className="py-1 border-r text-blue-700">{language === 'pt' ? 'Real' : 'Actual'}</div>
                  <div className="py-1 text-cyan-700">{language === 'pt' ? 'Orçamento' : 'Budget'}</div>
                </div>
                <div className="max-h-36 overflow-y-auto">
                  {quarterlyPeriods.map((row, idx) => (
                    <div key={row.quarter} className={`grid grid-cols-2 text-[10px] ${idx % 2 === 0 ? 'bg-white' : 'bg-slate-50'}`}>
                      <div className="py-1 px-1 border-r flex justify-center">
                        {row.real ? (
                          <button
                            onClick={() => togglePeriod(row.real)}
                            disabled={selectedCategory === 'annual'}
                            className={`px-2 py-0.5 rounded transition-all ${
                              selectedPeriods.includes(row.real)
                                ? 'bg-blue-500 text-white font-semibold'
                                : 'hover:bg-blue-100 text-slate-700'
                            } ${selectedCategory === 'annual' ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                          >
                            {row.quarter}
                          </button>
                        ) : (
                          <span className="text-slate-300">—</span>
                        )}
                      </div>
                      <div className="py-1 px-1 flex justify-center">
                        {row.budget ? (
                          <button
                            onClick={() => togglePeriod(row.budget)}
                            disabled={selectedCategory === 'annual'}
                            className={`px-2 py-0.5 rounded transition-all ${
                              selectedPeriods.includes(row.budget)
                                ? 'bg-cyan-500 text-white font-semibold'
                                : 'hover:bg-cyan-100 text-slate-700'
                            } ${selectedCategory === 'annual' ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                          >
                            {row.quarter}
                          </button>
                        ) : (
                          <span className="text-slate-300">—</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Right: Indicator Selector */}
          <div className="pl-2">
            <h2 className="font-serif text-sm font-semibold text-primary mb-3 flex items-center gap-2">
              <BarChart3 className="w-4 h-4 text-gold" />
              {language === 'pt' ? 'Selecionar Indicador' : 'Select Indicator'}
            </h2>
            <div className="flex flex-wrap gap-1.5">
              {availableIndicators.map(indicator => (
                <button
                  key={indicator.key}
                  onClick={() => setSelectedIndicator(indicator.key)}
                  className={`
                    flex items-center gap-1 px-2.5 py-1 rounded-md border transition-all text-xs
                    ${selectedIndicator === indicator.key
                      ? 'border-primary bg-primary text-white'
                      : 'border-slate-200 bg-white text-slate-600 hover:border-primary/50'
                    }
                  `}
                  data-testid={`indicator-selector-${indicator.key}`}
                >
                  {selectedIndicator === indicator.key && <Check className="w-3 h-3" />}
                  <span className="font-medium">{indicator.label[language]}</span>
                  <span className={`text-[10px] px-1 py-0.5 rounded ${
                    selectedIndicator === indicator.key ? 'bg-white/20' : 'bg-slate-100'
                  }`}>
                    {indicator.unit}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Comparison Chart */}
      <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
        <h2 className="font-serif text-lg font-semibold text-primary mb-4 flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-gold" />
          {language === 'pt' ? 'Evolução Comparativa' : 'Comparative Evolution'}
          <span className="ml-2 text-sm font-normal text-slate-500">
            — {currentIndicator?.label[language]} ({currentIndicator?.unit})
          </span>
        </h2>
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} barGap={12} margin={{ left: 20, right: 20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis 
                dataKey="period" 
                tick={{ fill: '#64748B', fontSize: 12 }} 
                interval={0}
                padding={{ left: 30, right: 400 }}
              />
              <YAxis tick={{ fill: '#64748B', fontSize: 12 }} />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: 'white', 
                  border: '1px solid #E2E8F0',
                  borderRadius: '8px',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                }}
                formatter={(value) => {
                  if (selectedIndicator === 'carats') {
                    return [value.toLocaleString() + ' cts', currentIndicator?.label[language]];
                  } else if (selectedIndicator === 'costPerCarat') {
                    return ['$' + value.toLocaleString(), currentIndicator?.label[language]];
                  } else if (selectedIndicator === 'ebitdaMargin') {
                    return [value.toFixed(1) + '%', currentIndicator?.label[language]];
                  } else {
                    return [value.toFixed(1) + ' M USD', currentIndicator?.label[language]];
                  }
                }}
              />
              <Bar 
                dataKey="value" 
                name={currentIndicator?.label[language]} 
                fill={currentIndicator?.color || colors.primary} 
                radius={[4, 4, 0, 0]}
                maxBarSize={80}
              >
                <LabelList 
                  dataKey="value" 
                  position="top" 
                  fill="#374151"
                  fontSize={11}
                  fontWeight={600}
                  formatter={(value) => {
                    if (selectedIndicator === 'carats') {
                      return value.toLocaleString();
                    } else if (selectedIndicator === 'costPerCarat') {
                      return '$' + value.toLocaleString();
                    } else if (selectedIndicator === 'ebitdaMargin') {
                      return value.toFixed(1) + '%';
                    } else {
                      return value.toFixed(1);
                    }
                  }}
                />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* KPI Comparison Table - Compact */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="p-4 border-b border-slate-200">
          <h2 className="font-serif text-sm font-semibold text-primary flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-gold" />
            {language === 'pt' ? 'Indicadores Comparativos' : 'Comparative Indicators'}
          </h2>
          <p className="text-xs text-slate-500 mt-0.5">
            {language === 'pt' 
              ? `Variações calculadas vs ${availablePeriods.find(p => p.id === sortedSelectedPeriods[0])?.label[language]}`
              : `Variances calculated vs ${availablePeriods.find(p => p.id === sortedSelectedPeriods[0])?.label[language]}`}
          </p>
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-slate-50">
                <th className="text-left py-2 px-4 text-xs font-semibold text-slate-700 border-b border-slate-200">
                  {language === 'pt' ? 'Indicador' : 'Indicator'}
                </th>
                {sortedSelectedPeriods.map(periodId => {
                  const period = availablePeriods.find(p => p.id === periodId);
                  return (
                    <th key={periodId} className="text-right py-2 px-4 text-xs font-semibold text-slate-700 border-b border-slate-200">
                      {period?.label[language]}
                      {periodId === sortedSelectedPeriods[0] && (
                        <span className="ml-1 text-[10px] bg-primary/10 text-primary px-1.5 py-0.5 rounded">
                          {language === 'pt' ? 'Base' : 'Base'}
                        </span>
                      )}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {kpiDefinitions.map((kpi, idx) => (
                <tr key={kpi.key} className={idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}>
                  <td className="py-2 px-4 border-b border-slate-100">
                    <span className="text-xs font-medium text-slate-700">{kpi.label[language]}</span>
                    <span className="text-[10px] text-slate-400 ml-1">({kpi.unit})</span>
                  </td>
                  {sortedSelectedPeriods.map(periodId => {
                    const value = periodData[periodId][kpi.key];
                    const variance = getVariance(kpi, periodId);
                    const isBase = periodId === sortedSelectedPeriods[0];
                    
                    let varianceColor = 'text-slate-500';
                    let varianceIcon = <Minus className="w-2.5 h-2.5" />;
                    
                    if (variance !== null) {
                      const isPositiveChange = kpi.inverse ? variance < 0 : variance > 0;
                      varianceColor = isPositiveChange ? 'text-emerald-600' : 'text-red-600';
                      varianceIcon = variance > 0 
                        ? <ArrowUpRight className="w-2.5 h-2.5" /> 
                        : <ArrowDownRight className="w-2.5 h-2.5" />;
                    }
                    
                    return (
                      <td key={periodId} className="py-2 px-4 text-right border-b border-slate-100">
                        <div className="flex flex-col items-end gap-0.5">
                          <span className="font-mono text-sm font-semibold text-primary">
                            {kpi.format(value)}
                          </span>
                          {!isBase && variance !== null && (
                            <span className={`flex items-center gap-0.5 text-[10px] font-medium ${varianceColor}`}>
                              {varianceIcon}
                              {variance > 0 ? '+' : ''}{variance.toFixed(1)}%
                            </span>
                          )}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default FinanceMultiPeriodComparative;
